(() => {
  'use strict';

  if (window.ajaxCartQuantityInitialized) return;
  window.ajaxCartQuantityInitialized = true;

  const ROOT_SELECTOR = '[data-ajax-main-cart]';
  const FORM_SELECTOR = '[data-ajax-cart-form]';
  const QUANTITY_SELECTOR =
    'input[name="updates[]"], input[name^="updates["]';

  let updateTimer = null;
  let requestInProgress = false;
  let updateQueued = false;

  const getRootUrl = () =>
    window.Shopify?.routes?.root || '/';

  const getCartUpdateUrl = () =>
    window.routes?.cart_update_url
      ? `${window.routes.cart_update_url}.js`.replace('.js.js', '.js')
      : `${getRootUrl()}cart/update.js`;

  const getCartRoot = () =>
    document.querySelector(ROOT_SELECTOR);

  const getCartForm = () =>
    getCartRoot()?.querySelector(FORM_SELECTOR);

  const getQuantityInputs = (form) =>
    Array.from(form.querySelectorAll(QUANTITY_SELECTOR));

  const normalizeQuantity = (input) => {
    const minimum = Number(input.min || 0);
    const value = Number.parseInt(input.value, 10);

    input.value = String(
      Number.isFinite(value)
        ? Math.max(minimum, value)
        : Math.max(minimum, 0)
    );

    return Number(input.value);
  };

  const showStatus = (root, message = '', isError = false) => {
    const status = root?.querySelector('[data-ajax-cart-status]');
    if (!status) return;

    status.textContent = message;
    status.hidden = !message;
    status.classList.toggle('is-error', isError);
  };

  const setLoading = (root, loading) => {
    if (!root) return;

    root.classList.toggle('is-updating', loading);
    root.setAttribute('aria-busy', loading ? 'true' : 'false');

    root.querySelectorAll(QUANTITY_SELECTOR).forEach((input) => {
      input.disabled = loading;
    });

    root.querySelectorAll(
      'button, input[type="submit"], input[type="button"]'
    ).forEach((button) => {
      if (button.name === 'checkout') return;
      button.disabled = loading;
    });
  };

  const updateExternalCartCount = (itemCount) => {
    if (typeof itemCount !== 'number') return;

    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = itemCount;
      element.hidden = itemCount < 1;
    });

    document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
      const count =
        bubble.querySelector('span[aria-hidden="true"]') ||
        bubble.querySelector('span');

      if (count) count.textContent = itemCount;

      bubble.hidden = itemCount < 1;
      bubble.classList.toggle('hidden', itemCount < 1);
    });
  };

  const replaceRenderedCartSection = (
    currentRoot,
    sectionId,
    renderedHtml
  ) => {
    if (!renderedHtml) {
      throw new Error('The updated cart section could not be rendered.');
    }

    const parser = new DOMParser();
    const documentFragment = parser.parseFromString(
      renderedHtml,
      'text/html'
    );

    const updatedRoot =
      documentFragment.querySelector(ROOT_SELECTOR);

    if (!updatedRoot) {
      throw new Error('The refreshed cart markup was not found.');
    }

    currentRoot.replaceWith(updatedRoot);

    const focusTarget =
      updatedRoot.querySelector(QUANTITY_SELECTOR);

    if (focusTarget) {
      focusTarget.focus({ preventScroll: true });
    }
  };

  const collectUpdates = (form) => {
    const inputs = getQuantityInputs(form);

    if (!inputs.length) {
      throw new Error(
        'No cart quantity inputs were found. Check cart-product-list.liquid.'
      );
    }

    return inputs.map(normalizeQuantity);
  };

  const dispatchCartUpdated = (cart) => {
    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        bubbles: true,
        detail: {
          cart,
          source: 'main-cart-ajax-quantity'
        }
      })
    );

    document.dispatchEvent(
      new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: {
          cart,
          source: 'main-cart-ajax-quantity'
        }
      })
    );
  };

  const updateCart = async () => {
    if (requestInProgress) {
      updateQueued = true;
      return;
    }

    const root = getCartRoot();
    const form = getCartForm();

    if (!root || !form) return;

    const sectionId = root.dataset.sectionId;
    if (!sectionId) {
      showStatus(root, 'The cart section ID is missing.', true);
      return;
    }

    requestInProgress = true;
    updateQueued = false;
    setLoading(root, true);
    showStatus(root, 'Updating cart…');

    try {
      const updates = collectUpdates(form);

      const response = await fetch(getCartUpdateUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          updates,
          sections: [sectionId],
          sections_url: window.location.pathname
        })
      });

      const cart = await response.json();

      if (!response.ok || cart.status) {
        throw new Error(
          cart.description ||
          cart.message ||
          'The cart could not be updated.'
        );
      }

      updateExternalCartCount(cart.item_count);

      const renderedSection =
        cart.sections?.[sectionId];

      replaceRenderedCartSection(
        root,
        sectionId,
        renderedSection
      );

      dispatchCartUpdated(cart);
    } catch (error) {
      console.error('AJAX cart quantity update error:', error);

      const latestRoot = getCartRoot() || root;
      showStatus(
        latestRoot,
        error?.message || 'The cart could not be updated.',
        true
      );

      setLoading(latestRoot, false);
    } finally {
      requestInProgress = false;

      if (updateQueued) {
        window.setTimeout(updateCart, 0);
      }
    }
  };

  const scheduleUpdate = (delay = 350) => {
    window.clearTimeout(updateTimer);
    updateTimer = window.setTimeout(updateCart, delay);
  };

  const INCREASE_SELECTORS = [
    '[data-quantity-plus]',
    '[data-quantity-increase]',
    '[data-increase]',
    '[data-qty-plus]',
    '[data-action="increase"]',
    '[data-action="increment"]',
    '.quantity-plus',
    '.qty-plus',
    '.quantity-increase',
    '.quantity-increment',
    '.increment',
    '.plus',
    '.js-qty__adjust--plus',
    '[aria-label*="increase" i]',
    '[aria-label*="increment" i]',
    '[aria-label*="add one" i]'
  ].join(',');

  const DECREASE_SELECTORS = [
    '[data-quantity-minus]',
    '[data-quantity-decrease]',
    '[data-decrease]',
    '[data-qty-minus]',
    '[data-action="decrease"]',
    '[data-action="decrement"]',
    '.quantity-minus',
    '.qty-minus',
    '.quantity-decrease',
    '.quantity-decrement',
    '.decrement',
    '.minus',
    '.js-qty__adjust--minus',
    '[aria-label*="decrease" i]',
    '[aria-label*="decrement" i]',
    '[aria-label*="subtract one" i]'
  ].join(',');

  const getQuantityDirection = (control) => {
    if (control.matches(INCREASE_SELECTORS)) return 1;
    if (control.matches(DECREASE_SELECTORS)) return -1;

    const controlValue = String(
      control.value ||
      control.getAttribute('data-value') ||
      control.textContent ||
      ''
    )
      .replace(/\s+/g, '')
      .trim()
      .toLowerCase();

    if (['+', '＋', 'plus', 'increase', 'increment'].includes(controlValue)) {
      return 1;
    }

    if (['-', '−', '–', 'minus', 'decrease', 'decrement'].includes(controlValue)) {
      return -1;
    }

    const className = String(control.className || '').toLowerCase();

    if (
      className.includes('plus') ||
      className.includes('increase') ||
      className.includes('increment')
    ) {
      return 1;
    }

    if (
      className.includes('minus') ||
      className.includes('decrease') ||
      className.includes('decrement')
    ) {
      return -1;
    }

    return 0;
  };

  const findNearbyQuantityInput = (control) => {
    const likelyWrapper = control.closest(
      [
        '[data-quantity-selector]',
        '[data-quantity-wrapper]',
        '.quantity-selector',
        '.quantity-wrapper',
        '.quantity-control',
        '.quantity-controls',
        '.qty-wrapper',
        '.qty-selector',
        '.js-qty',
        '.cart-quantity',
        '.cart-item__quantity',
        '.cart-product-quantity'
      ].join(',')
    );

    const wrappedInput =
      likelyWrapper?.querySelector(QUANTITY_SELECTOR);

    if (wrappedInput) return wrappedInput;

    let element = control.parentElement;

    while (element && !element.matches(FORM_SELECTOR)) {
      const quantityInput =
        element.querySelector?.(QUANTITY_SELECTOR);

      if (quantityInput) return quantityInput;

      element = element.parentElement;
    }

    return null;
  };

  const changeQuantityFromControl = (input, direction) => {
    const currentValue =
      Number.parseInt(input.value, 10) || 0;

    const minimum = Number.isFinite(Number(input.min))
      ? Number(input.min)
      : 0;

    const maximum =
      input.max !== '' && Number.isFinite(Number(input.max))
        ? Number(input.max)
        : Number.POSITIVE_INFINITY;

    const step =
      Number.isFinite(Number(input.step)) && Number(input.step) > 0
        ? Number(input.step)
        : 1;

    const nextValue = Math.min(
      maximum,
      Math.max(minimum, currentValue + direction * step)
    );

    if (nextValue === currentValue) return false;

    input.value = String(nextValue);

    input.dispatchEvent(
      new Event('input', {
        bubbles: true
      })
    );

    input.dispatchEvent(
      new Event('change', {
        bubbles: true
      })
    );

    return true;
  };

  document.addEventListener('input', (event) => {
    const input = event.target.closest(QUANTITY_SELECTOR);
    if (!input || !input.closest(ROOT_SELECTOR)) return;

    normalizeQuantity(input);
    scheduleUpdate();
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest(QUANTITY_SELECTOR);
    if (!input || !input.closest(ROOT_SELECTOR)) return;

    normalizeQuantity(input);
    scheduleUpdate(150);
  });

  document.addEventListener(
    'click',
    (event) => {
      const root = event.target.closest(ROOT_SELECTOR);
      if (!root) return;

      const control = event.target.closest(
        'button, input[type="button"], input[type="submit"], a'
      );

      if (!control) return;

      const href = control.getAttribute('href') || '';

      const isRemoveControl =
        control.matches(
          [
            '[data-cart-remove]',
            '.cart-remove',
            '.remove',
            '[aria-label*="Remove" i]'
          ].join(',')
        ) ||
        href.includes('quantity=0');

      if (isRemoveControl) return;

      const direction = getQuantityDirection(control);
      if (!direction) return;

      const quantityInput = findNearbyQuantityInput(control);
      if (!quantityInput) {
        console.warn(
          'AJAX cart: a plus/minus control was found, but its quantity input was not.'
        );
        return;
      }

      /*
       * Handle the quantity ourselves. This is delegated and continues to
       * work after the cart section is replaced with refreshed HTML.
       */
      event.preventDefault();
      event.stopImmediatePropagation();

      changeQuantityFromControl(
        quantityInput,
        direction
      );
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target.closest(FORM_SELECTOR);
      if (!form) return;

      const submitter = event.submitter;

      if (submitter?.name === 'checkout') {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      scheduleUpdate(0);
    },
    true
  );


  // new code
  // Update cart icon count after any AJAX add to cart
const refreshCartCount = async () => {

  try {

    const response = await fetch('/cart.js');
    const cart = await response.json();

    updateExternalCartCount(cart.item_count);

    // Your custom cart icon
    const cartToggler = document.querySelector('.cart-toggler');

    if(cartToggler){

      cartToggler.setAttribute(
        'data-total-count',
        cart.item_count
      );

      cartToggler.setAttribute(
        'data-aid',
        'cart-item-count-' + cart.item_count
      );

    }

  } catch(error){

    console.error('Cart count refresh error:', error);

  }

};


// Listen for Shopify cart updates
document.addEventListener(
  'cart:updated',
  refreshCartCount
);


// Fallback for collection add-to-cart buttons
document.addEventListener(
  'click',
  function(event){

    const button = event.target.closest('.collection-add-to-cart');

    if(!button) return;

    setTimeout(()=>{
      refreshCartCount();
    },800);

  }
);
})();
